/**
 * How many of one item go in the box: "Add", then − 2 +.
 *
 * Starts as a single Add button so an untouched menu reads as a menu rather
 * than a wall of zeroes. Every control keeps the 44pt touch target even
 * though the stepper itself is compact.
 */

import { memo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { colors, radius, sizing, space } from '../../theme';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

/** The browser's focus ring would repeat the selected state; it is dropped. */
const NO_OUTLINE = { outlineStyle: 'none' } as unknown as ViewStyle;
const BUTTON = 36;
/** Pads the visible 36pt circle out to the 44pt touch minimum. */
const SLOP = (sizing.touchTarget - BUTTON) / 2;

export interface ItemStepperProps {
  value: number;
  max: number;
  onChange: (value: number) => void;
  /** The item's name, for the screen reader: "Pepsi, 2 in each box". */
  name: string;
  disabled?: boolean;
  testID?: string;
}

export const ItemStepper = memo(function ItemStepper({
  value,
  max,
  onChange,
  name,
  disabled = false,
  testID = 'item-stepper',
}: ItemStepperProps) {
  const t = useT();

  if (value === 0) {
    return (
      <Pressable
        testID={`${testID}-add`}
        onPress={() => onChange(1)}
        disabled={disabled}
        hitSlop={SLOP}
        accessibilityRole="button"
        accessibilityLabel={`${t('Add')} ${name}`}
        style={({ pressed }) => [
          styles.add,
          disabled && styles.addDisabled,
          pressed && styles.pressed,
          NO_OUTLINE,
        ]}
      >
        <Text variant="button" tone={disabled ? 'disabled' : 'brand'}>
          {t('Add')}
        </Text>
      </Pressable>
    );
  }

  const canIncrease = value < max && !disabled;
  return (
    <View
      testID={testID}
      style={styles.stepper}
      accessibilityLabel={`${name}, ${value} ${t('in each box')}`}
    >
      <Pressable
        testID={`${testID}-decrease`}
        onPress={() => onChange(value - 1)}
        disabled={disabled}
        hitSlop={SLOP}
        accessibilityRole="button"
        accessibilityLabel={`${t('One fewer')} ${name}`}
        style={({ pressed }) => [styles.round, pressed && styles.pressed, NO_OUTLINE]}
      >
        <Text variant="sectionTitle" tone="brand">−</Text>
      </Pressable>
      <Text variant="bodyStrong" style={styles.count} testID={`${testID}-count`}>
        {value}
      </Text>
      <Pressable
        testID={`${testID}-increase`}
        onPress={() => onChange(value + 1)}
        disabled={!canIncrease}
        hitSlop={SLOP}
        accessibilityRole="button"
        accessibilityLabel={`${t('One more')} ${name}`}
        accessibilityHint={!canIncrease && !disabled ? `${t('Up to')} ${max}` : undefined}
        style={({ pressed }) => [
          styles.round,
          !canIncrease && styles.roundDisabled,
          pressed && styles.pressed,
          NO_OUTLINE,
        ]}
      >
        <Text variant="sectionTitle" tone={canIncrease ? 'brand' : 'disabled'}>+</Text>
      </Pressable>
    </View>
  );
});

const styles = StyleSheet.create({
  add: {
    minWidth: 76,
    height: BUTTON,
    paddingHorizontal: space.lg,
    borderRadius: radius.full,
    borderWidth: 1.5,
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addDisabled: {
    borderColor: colors.border.default,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    padding: space.xxs,
    borderRadius: radius.full,
    backgroundColor: colors.bg.selected,
  },
  round: {
    width: BUTTON,
    height: BUTTON,
    borderRadius: radius.full,
    backgroundColor: colors.bg.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundDisabled: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  count: {
    minWidth: 24,
    textAlign: 'center',
  },
  pressed: {
    opacity: 0.7,
  },
});
